/* BD — PostHog cart tracking + checkout identity / vehicle stamping.
 *
 * Jobs:
 *  1. Capture an `add_to_cart` PostHog event for every successful POST to
 *     /cart/add(.js). Hooks window.fetch so all add paths are covered in one
 *     place: the standard product form (product-form.js), the fitment buy box
 *     (bd-pdp-fitment.js, posts an `items` array), and bd-set-qty.
 *  2. Carry the visitor's PostHog distinct id into checkout so the
 *     "PostHog checkout" custom pixel (tools/shopify/pixels/) can stitch
 *     checkout_* events back to this person — theme JS can't run in Shopify
 *     checkout. Three carriers, because each covers a path the others miss:
 *       a. cart attribute `_ph_did`, stamped at page load (once per session)
 *          and again after every add — covers cart → checkout;
 *       b. hidden line-item property `properties[_ph_did]` injected into
 *          every product form — covers dynamic Buy-it-now / Shop Pay buttons,
 *          which build the checkout from the form and skip the cart entirely
 *          (2026-09-15: only 4 of 25 checkouts were stitched before this);
 *       c. the same property injected into JSON / FormData bodies of
 *          /cart/add.js calls made by BD's own scripts.
 *     Underscore-prefixed properties are hidden from the shopper in cart,
 *     checkout, and order emails.
 *  3. Stamp the shopper's saved vehicle (BDFitCore, localStorage
 *     bdf_vehicle_v4) into cart attribute `Vehicle` so the order carries it
 *     under "Additional details" for the fulfillment team. Per-line fitment
 *     detail is written separately by bd-set-fitcheck.js / bd-pdp-fitment.js.
 */
(function () {
  'use strict';
  if (window.__bdPHCartHooked) return;
  window.__bdPHCartHooked = true;

  var STAMP_KEY = '_bd_cart_stamp_v2';
  var DID_PROP = '_ph_did';
  var VEHICLE_KEY = 'bdf_vehicle_v4';

  function ph() {
    return window.posthog && typeof window.posthog.capture === 'function' ? window.posthog : null;
  }

  // distinct id is only meaningful once posthog-js has actually loaded (the
  // snippet stub returns undefined); poll briefly rather than race it.
  function whenPostHogReady(cb) {
    var tries = 0;
    (function tick() {
      var p = ph();
      var did = p && p.__loaded && typeof p.get_distinct_id === 'function' ? p.get_distinct_id() : null;
      if (did) return cb(did);
      if (++tries < 100) setTimeout(tick, 100);
    })();
  }

  function savedVehicleLabel() {
    try {
      var s = JSON.parse(localStorage.getItem(VEHICLE_KEY));
      return s && s.vehicle && s.vehicle.label ? String(s.vehicle.label) : '';
    } catch (e) {
      return '';
    }
  }
  // ---- cart attributes (_ph_did + Vehicle) ----
  function stampCart(did, force) {
    var vehicle = savedVehicleLabel();
    var sig = (did || '') + '|' + vehicle;
    if (!force) {
      try {
        if (sessionStorage.getItem(STAMP_KEY) === sig) return;
      } catch (e) {
        /* storage blocked — stamp anyway */
      }
    }
    var attributes = { Vehicle: vehicle };
    if (did) attributes[DID_PROP] = did;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    })
      .then(function (r) {
        if (r.ok) {
          try {
            sessionStorage.setItem(STAMP_KEY, sig);
          } catch (e) {}
        }
      })
      .catch(function () {});
  }

  // ---- hidden line-item property on every product form ----
  var currentDid = null;
  function injectForms(root) {
    if (!currentDid) return;
    var forms = (root || document).querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      // the price block renders a bare `payment-terms` form for installments —
      // never submitted, skip it
      if (form.classList.contains('payment-terms')) continue;
      var input = form.querySelector('input[name="properties[' + DID_PROP + ']"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + DID_PROP + ']';
        form.appendChild(input);
      }
      if (input.value !== currentDid) input.value = currentDid;
    }
  }

  function watchForms() {
    injectForms();
    if (!window.MutationObserver) return;
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        injectForms();
      }, 50);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // ---- inject into /cart/add.js request bodies from BD scripts ----
  function withDid(init) {
    if (!currentDid || !init || !init.body) return init;
    try {
      var body = init.body;
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (!body.has('properties[' + DID_PROP + ']')) body.append('properties[' + DID_PROP + ']', currentDid);
        return init;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        if (!body.has('properties[' + DID_PROP + ']')) body.append('properties[' + DID_PROP + ']', currentDid);
        return init;
      }
      if (typeof body === 'string' && body.charAt(0) === '{') {
        var json = JSON.parse(body);
        if (Array.isArray(json.items)) {
          json.items.forEach(function (it) {
            it.properties = it.properties || {};
            if (!it.properties[DID_PROP]) it.properties[DID_PROP] = currentDid;
          });
        } else if (json.id) {
          json.properties = json.properties || {};
          if (!json.properties[DID_PROP]) json.properties[DID_PROP] = currentDid;
        }
        var next = {};
        for (var k in init) next[k] = init[k];
        next.body = JSON.stringify(json);
        return next;
      }
    } catch (e) {
      /* leave the request untouched */
    }
    return init;
  }

  // ---- add_to_cart event ----
  function captureAdd(items) {
    var p = ph();
    if (!p) return;
    var value = 0;
    var count = 0;
    var lines = items.map(function (it) {
      var lineCents =
        it.final_line_price != null ? it.final_line_price : (it.price || 0) * (it.quantity || 1);
      value += lineCents / 100;
      count += it.quantity || 0;
      return {
        product_id: it.product_id,
        variant_id: it.variant_id || it.id,
        sku: it.sku,
        title: it.product_title || it.title,
        variant: it.variant_title,
        handle: it.handle,
        quantity: it.quantity,
        price: (it.price || 0) / 100,
      };
    });
    p.capture('add_to_cart', {
      items: lines,
      item_count: count,
      value: Math.round(value * 100) / 100,
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      vehicle: savedVehicleLabel() || undefined,
    });
    stampCart(p.get_distinct_id(), true);
  }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isAdd = /\/cart\/add(\.js)?([?#]|$)/.test(url);
    var args = arguments;
    if (isAdd && init) {
      args = [input, withDid(init)];
    }
    var res = origFetch.apply(this, args);
    if (!isAdd) return res;
    return res.then(function (response) {
      if (response && response.ok) {
        response
          .clone()
          .json()
          .then(function (json) {
            var items = json && (json.items || (json.id ? [json] : null));
            if (items && items.length) captureAdd(items);
          })
          .catch(function () {});
      }
      return response;
    });
  };

  // ---- boot ----
  whenPostHogReady(function (did) {
    currentDid = did;
    watchForms();
    stampCart(did, false);
  });
  window.addEventListener('bd:vehicle-changed', function () {
    stampCart(currentDid, true);
  });
})();
